import { Typography } from "@/frontend/shared/ui/Typography";
import styles from "./TeamSection.module.scss";
import { NormalButton } from "@/frontend/shared/ui/Button";
import { useState } from "react";
import { Input } from "@/frontend/shared/ui/Input";
import { EditIcon, LinkedinIcon, TrashIcon } from "@/frontend/shared/assets/icons";
import { Avatar } from "@/frontend/shared/ui/Avatar/Avatar";
import { UploadLogo } from "@/frontend/shared/ui/UploadLogo/UploadLogo";

type TTeamMember = {
  firstName: string;
  lastName: string;
  title: string;
  linkedIn: string;
  imageUrl: string;
}

export function TeamSection() {
  const [teamMembers, setTeamMembers] = useState<TTeamMember[]>([
    {
      firstName: "John",
      lastName: "Doe",
      title: "CEO",
      linkedIn: "https://www.linkedin.com/",
      imageUrl: "",
    },
    {
      firstName: "Jane",
      lastName: "Doe",
      title: "CTO",
      linkedIn: "https://www.linkedin.com/",
      imageUrl: "",
    }
  ]);
  const [formData, setFormData] = useState<TTeamMember>({
    firstName: "",
    lastName: "",
    title: "",
    linkedIn: "",
    imageUrl: "",
  });
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editIndex, setEditIndex] = useState<number | null>(null);

  function handleFormSubmit() {
    if (editIndex !== null) {
      setTeamMembers((prev) => prev.map((member, index) => index === editIndex ? formData : member));
    } else {
      setTeamMembers((prev) => [...prev, formData]);
    }
    setIsFormOpen(false);
    setFormData({
      firstName: "",
      lastName: "",
      title: "",
      linkedIn: "",
      imageUrl: "",
    });
    setEditIndex(null);
  }

  function editTeamMember(index: number) {
    setIsFormOpen(true);
    setFormData(teamMembers[index]);
    setEditIndex(index);
  }

  function deleteTeamMember(index: number) {
    setTeamMembers((prev) => prev.filter((_, i) => i !== index));
  }

  function TeamMember({ member, index }: { member: TTeamMember, index: number }) {
    return <div className={styles.teamMem}>
      <div className={styles.teamMemImg}>
        {
          member.imageUrl ? <img src={member.imageUrl} alt="team member" /> : <div className={styles.defaultImage}>
            <Avatar />
          </div>
        }
      </div>
      <div className={styles.teamMemInfo}>
        <div className={styles.tMFlex}>
          <div className={styles.teamMemTop}>
            <Typography variant="label-4" color="greyscale900">
              {member.firstName} {member.lastName}
            </Typography>
            <div>
              <a href={member.linkedIn} target="_blank" rel="noreferrer">
                <LinkedinIcon />
              </a>
            </div>
          </div>
          <div className={styles.teamIcons}>
            <EditIcon onClick={() => editTeamMember(index)} />
            <TrashIcon onClick={() => deleteTeamMember(index)} />
          </div>
        </div>
        <Typography variant="body-2" color="greyscale800">
          {member.title}
        </Typography>
      </div>
    </div>
  }

  function teamMemberForm() {
    return (
      <div className={styles.formContainer}>
        <UploadLogo
          image={formData.imageUrl}
          onRemove={() => setFormData((prev) => ({ ...prev, imageUrl: "" }))}
          onUpload={(e) => setFormData((prev) => ({ ...prev, imageUrl: e }))}
          text="Upload Image"
        />
        <div className={styles.inputContainer}>
          <Typography variant="label-4" color="greyscale800">
            First name
          </Typography>
          <Input placeholder="First name" inputSize="small" value={formData.firstName} onChange={(e) => setFormData((prev) => ({ ...prev, firstName: e.target.value }))} />
        </div>
        <div className={styles.inputContainer}>
          <Typography variant="label-4" color="greyscale800">
            Last name
          </Typography>
          <Input placeholder="Last name" inputSize="small" value={formData.lastName} onChange={(e) => setFormData((prev) => ({ ...prev, lastName: e.target.value }))} />
        </div>
        <div className={styles.inputContainer}>
          <Typography variant="label-4" color="greyscale800">
            Title
          </Typography>
          <Input placeholder="Title" inputSize="small" value={formData.title} onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))} />
        </div>
        <div className={styles.inputContainer}>
          <Typography variant="label-4" color="greyscale800">
            LinkedIn
          </Typography>
          <Input placeholder="LinkedIn" inputSize="small" value={formData.linkedIn} onChange={(e) => setFormData((prev) => ({ ...prev, linkedIn: e.target.value }))} />
        </div>
        <div className={styles.formButtons}>
          <NormalButton size="small" onClick={handleFormSubmit}>Save</NormalButton>
          <NormalButton size="small" variant="secondary" color="greyscale600" onClick={() => { setIsFormOpen(false); setEditIndex(null); }}>Cancel</NormalButton>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <Typography variant="heading-6" color="greyscale900">
        Team
      </Typography>

      <div className={styles.teamMemCont}>
        {teamMembers.map((member, index) => (
          <TeamMember key={index} index={index} member={member} />
        ))}
      </div>

      {isFormOpen && teamMemberForm()}

      <NormalButton size="small" className={styles.addButton} color="greyscale600" variant="secondary" onClick={() => { setIsFormOpen(true); setEditIndex(null); }}>Add team member</NormalButton>

      <div className={styles.bottomButtonsContainer}>
        <NormalButton size="large">Proceed</NormalButton>
        <NormalButton size="large" variant="secondary" color="greyscale800">
          Step back
        </NormalButton>
      </div>
    </div>
  );
}
